import { createConsumer } from '@rails/actioncable';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import { namespace } from 'client-portal/adapters/application';
import { task, timeout } from 'ember-concurrency';
import Service, { service } from '@ember/service';

const TOKEN_EXPIRATION = 15 * 60 * 1000;
const REFRESH_BUFFER = 60000;
export default class ActionCableService extends Service {
  @service fastboot;
  @service store;

  consumer = null;
  token = null;
  subscriptions = new Map();

  async createSubscription(key, identifier, callbacks) {
    if (this.subscriptions.has(key)) {
      return this.subscriptions.get(key).subscription;
    }

    await this.#ensureConsumer();
    if (!this.consumer) return null;

    if (!this.refreshTokenTask.isRunning) {
      this.refreshTokenTask.perform();
    }

    const subscription = this.consumer.subscriptions.create(identifier, callbacks);
    this.subscriptions.set(key, subscription);

    return subscription;
  }

  async #ensureConsumer() {
    if (this.consumer) return;

    await this.getConsumer();
  }

  unsubscribe(key) {
    const subscription = this.subscriptions.get(key);
    if (!subscription) return;

    subscription.unsubscribe();
    this.subscriptions.delete(key);
  }

  async getConsumer() {
    if (this.fastboot.isFastBoot) return null;

    if (this.consumer) return this.consumer;

    return this.#fetchTokenAndCreateConsumer();
  }

  async #fetchTokenAndCreateConsumer() {
    await this.getActionCableToken.perform();
    if (!this.token) return null;

    this.consumer = this.createNewConsumer(() => {
      return `/client-portal-websocket?access_token=${this.token}`;
    });

    return this.consumer;
  }

  createNewConsumer(cableUrl) {
    return createConsumer(cableUrl);
  }

  getActionCableToken = task({ drop: true }, async () => {
    const adapter = this.store.adapterFor('application');
    try {
      const response = await adapter.ajax(`/${namespace}/sessions/websocket-token`, 'POST');
      this.token = response.token;
    } catch {
      this.token = null;
    }
  });

  refreshTokenTask = task({ restartable: true }, async () => {
    do {
      await timeout(this.#refreshTimeout);
      this.token = null;
      await this.getActionCableToken.perform();
    } while (!isEmberTesting());
  });

  get #refreshTimeout() {
    return isEmberTesting() ? 0 : TOKEN_EXPIRATION - REFRESH_BUFFER;
  }

  disconnect() {
    this.token = null;
    this.subscriptions.clear();
    this.refreshTokenTask.cancelAll();
    if (this.consumer) {
      this.consumer.disconnect();
      this.consumer = null;
    }
  }

  willDestroy() {
    super.willDestroy();
    this.disconnect();
  }
}
